import React, { useState, useRef, useEffect } from 'react';
import { Send, User, Bot, Copy, RotateCcw, Check, Smile, TvIcon, Briefcase, Zap, MessageSquare, HelpCircle, ChevronUp, ChevronDown } from 'lucide-react';
import { ChatMessage } from '../types';
import { generateAssistantResponse, generateContextCards, generateContextCardsWithAI, ContextCard } from '../services/openai';
import { supabase, TABLES, isTestMode, checkOpenAIServer, getCachedContextCards, saveContextCardsToCache, shouldRegenerateContextCards } from '../services/supabase';
import { Assistant } from '../types';
import ContextCardButton from './ContextCardButton';

interface ChatInterfaceProps {
    assistant: Assistant;
    onShouldReducePaddingChange?: (shouldReduce: boolean) => void;
}

// Icon mapping for context cards
const iconMap: Record<string, React.ReactNode> = {
    'Smile': <Smile />,
    'Briefcase': <Briefcase />,
    'Zap': <Zap />,
    'TvIcon': <TvIcon />,
    'HelpCircle': <HelpCircle />
};

const ChatInterface: React.FC<ChatInterfaceProps> = ({ assistant, onShouldReducePaddingChange }) => {
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showTyping, setShowTyping] = useState(false);
    const [sessionId, setSessionId] = useState<string>('');
    const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
    const [clickedCards, setClickedCards] = useState<Set<string>>(new Set());
    const [cardMessageMap, setCardMessageMap] = useState<Map<string, string>>(new Map());
    const [isAskAboutCollapsed, setIsAskAboutCollapsed] = useState(false);
    const [isPanelUp, setIsPanelUp] = useState(false);
    const [showFloatingHeader, setShowFloatingHeader] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const touchStartY = useRef<number>(0);
    const touchStartTime = useRef<number>(0);

    // Generate a session ID when the component mounts
    useEffect(() => {
        const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        setSessionId(newSessionId);
    }, [assistant]);

    // Auto-collapse Ask About section when messages are added
    useEffect(() => {
        if (messages.length > 0) {
            setIsAskAboutCollapsed(true);
        }
    }, [messages.length]);

    // Calculate shouldReducePadding based on messages and panel state
    const shouldReducePadding = messages.length > 0 || isPanelUp;

    // Notify parent component when padding should be reduced (when messages exist or panel is up)
    useEffect(() => {
        onShouldReducePaddingChange?.(shouldReducePadding);
    }, [shouldReducePadding, onShouldReducePaddingChange]);

    // Scroll detection for floating header
    useEffect(() => {
        const messagesContainer = messagesContainerRef.current;
        if (!messagesContainer) return;

        const handleScroll = () => {
            const scrollTop = messagesContainer.scrollTop;
            setShowFloatingHeader(scrollTop > 100);
        };

        messagesContainer.addEventListener('scroll', handleScroll);
        return () => messagesContainer.removeEventListener('scroll', handleScroll);
    }, []);

    // Touch handlers for mobile panel dragging
    const handleTouchStart = (e: React.TouchEvent) => {
        touchStartY.current = e.touches[0].clientY;
        touchStartTime.current = Date.now();
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        const currentY = e.touches[0].clientY;
        const deltaY = touchStartY.current - currentY;

        // If dragging up more than 50px, move panel up
        if (deltaY > 50) {
            setIsPanelUp(true);
        }
        // If dragging down more than 50px, move panel down
        else if (deltaY < -50) {
            setIsPanelUp(false);
        }
    };

    const handleTouchEnd = () => {
        // Panel stays in the position it was dragged to
        // Only auto-move up if there are messages and panel is down
        if (messages.length > 0 && !isPanelUp) {
            setIsPanelUp(true);
        }
    };

    // Generate context cards
    const [contextCards, setContextCards] = useState<Array<{ id: string; icon: React.ReactNode; emoji: string; title: string; prompt: string }>>([]);

    useEffect(() => {
        const loadContextCards = async () => {
            try {
                let cards: ContextCard[];

                if (isTestMode) {
                    // In test mode, try AI generation first, then fallback to predefined cards
                    try {
                        console.log('Test mode: Attempting AI generation for context cards');
                        cards = await generateContextCardsWithAI(assistant);
                        console.log('Test mode: Successfully generated AI context cards');
                    } catch (error) {
                        console.error('Test mode: AI generation failed, using fallback cards:', error);
                        cards = generateContextCards(assistant);
                    }
                } else {
                    // First, try to get cached cards
                    const cachedCards = await getCachedContextCards(assistant.id);

                    if (cachedCards) {
                        // Use cached cards if they exist
                        cards = cachedCards;
                    } else {
                        // No cached cards found, check if we need to regenerate
                        const needsRegeneration = await shouldRegenerateContextCards(assistant.id, assistant);

                        if (needsRegeneration) {
                            // Generate new cards with AI
                            try {
                                cards = await generateContextCardsWithAI(assistant);
                                // Save to cache for future use
                                await saveContextCardsToCache(assistant.id, cards, assistant);
                            } catch (error) {
                                console.error('Error generating AI context cards, using fallback:', error);
                                cards = generateContextCards(assistant);
                            }
                        } else {
                            // Fallback to predefined cards
                            cards = generateContextCards(assistant);
                        }
                    }
                }

                // Map icons to React components
                const cardsWithIcons = cards.map(card => ({
                    id: card.id,
                    title: card.title,
                    prompt: card.prompt,
                    emoji: card.emoji,
                    icon: iconMap[card.icon] || <HelpCircle />
                }));

                setContextCards(cardsWithIcons);
            } catch (error) {
                console.error('Error loading context cards:', error);
                // Fallback to predefined cards
                const fallbackCards = generateContextCards(assistant).map(card => ({
                    id: card.id,
                    title: card.title,
                    prompt: card.prompt,
                    emoji: card.emoji,
                    icon: iconMap[card.icon] || <HelpCircle />
                }));
                setContextCards(fallbackCards);
            }
        };

        loadContextCards();
    }, [assistant, isTestMode]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, showTyping]);

    // Auto-resize textarea
    const adjustTextareaHeight = () => {
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.style.height = 'auto';
            // Only expand if there's content, otherwise keep it at the default height
            if (inputMessage.trim()) {
                textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
            } else {
                textarea.style.height = 'auto'; // Default height for empty textarea
            }
        }
    };

    useEffect(() => {
        adjustTextareaHeight();
    }, [inputMessage]);

    const copyToClipboard = async (text: string, messageId: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedMessageId(messageId);
            setTimeout(() => setCopiedMessageId(null), 2000);
        } catch (err) {
            console.error('Failed to copy text: ', err);
        }
    };

    const handleContextCardClick = async (cardId: string, prompt: string) => {
        if (isLoading) return;

        // Check if card was already clicked
        if (clickedCards.has(cardId)) {
            // Find the message ID for this card and scroll to it
            const messageId = cardMessageMap.get(cardId);
            if (messageId) {
                const messageElement = document.getElementById(`message-${messageId}`);
                if (messageElement) {
                    messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    // Add a brief highlight effect using JavaScript
                    messageElement.style.transform = 'scale(1.1)';
                    messageElement.style.transition = 'transform 0.4s ease-out';
                    messageElement.style.zIndex = '10';
                    setTimeout(() => {
                        messageElement.style.transform = 'scale(1)';
                        setTimeout(() => {
                            messageElement.style.transition = '';
                            messageElement.style.zIndex = '';
                        }, 400);
                    }, 200);
                }
            }
            return;
        }

        const userMessage: ChatMessage = {
            id: Date.now().toString(),
            role: 'user',
            content: prompt,
            timestamp: new Date(),
        };

        setMessages(prev => [...prev, userMessage]);

        // Mark card as clicked and store message ID
        setClickedCards(prev => new Set(Array.from(prev).concat(cardId)));
        setCardMessageMap(prev => new Map(prev.set(cardId, userMessage.id)));

        setIsLoading(true);

        // Add a delay before showing typing indicator to feel more natural
        setTimeout(() => {
            setShowTyping(true);
        }, 500);

        try {
            let response: string;

            if (isTestMode) {
                // Check if OpenAI server is available even in test mode
                const openAIAvailable = await checkOpenAIServer();
                if (openAIAvailable) {
                    // Use real OpenAI response if server is available
                    response = await generateAssistantResponse(prompt, assistant);
                } else {
                    // Mock response if OpenAI server is not available
                    response = `Hello! I'm ${assistant.name}. This is a test response since you're running in test mode. In production, I would use the OpenAI API to generate a real response based on my profile.`;
                }
            } else {
                // Generate AI response
                response = await generateAssistantResponse(prompt, assistant);
            }

            // Hide typing indicator before showing response
            setShowTyping(false);

            const assistantMessage: ChatMessage = {
                id: (Date.now() + 1).toString(),
                role: 'assistant',
                content: response,
                timestamp: new Date(),
            };

            setMessages(prev => [...prev, assistantMessage]);

            // Save conversation to database (skip in test mode)
            if (!isTestMode) {
                await supabase.from(TABLES.CONVERSATIONS).insert([
                    {
                        assistant_id: assistant.id,
                        session_id: sessionId,
                        user_message: prompt,
                        assistant_response: response,
                    },
                ]);
            }

        } catch (error) {
            console.error('Error generating response:', error);
            setShowTyping(false);
            const errorMessage: ChatMessage = {
                id: (Date.now() + 1).toString(),
                role: 'assistant',
                content: 'Sorry, I encountered an error. Please try again.',
                timestamp: new Date(),
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSendMessage = async () => {
        if (!inputMessage.trim() || isLoading) return;

        const userMessage: ChatMessage = {
            id: Date.now().toString(),
            role: 'user',
            content: inputMessage,
            timestamp: new Date(),
        };

        setMessages(prev => [...prev, userMessage]);
        setInputMessage('');
        setIsLoading(true);

        // Reset textarea height
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
        }

        // Add a delay before showing typing indicator to feel more natural
        setTimeout(() => {
            setShowTyping(true);
        }, 500);

        try {
            let response: string;

            if (isTestMode) {
                // Check if OpenAI server is available even in test mode
                const openAIAvailable = await checkOpenAIServer();
                if (openAIAvailable) {
                    // Use real OpenAI response if server is available
                    response = await generateAssistantResponse(inputMessage, assistant);
                } else {
                    // Mock response if OpenAI server is not available
                    response = `Hello! I'm ${assistant.name}. This is a test response since you're running in test mode. In production, I would use the OpenAI API to generate a real response based on my profile.`;
                }
            } else {
                // Generate AI response
                response = await generateAssistantResponse(inputMessage, assistant);
            }

            // Hide typing indicator before showing response
            setShowTyping(false);

            const assistantMessage: ChatMessage = {
                id: (Date.now() + 1).toString(),
                role: 'assistant',
                content: response,
                timestamp: new Date(),
            };

            setMessages(prev => [...prev, assistantMessage]);

            // Save conversation to database (skip in test mode)
            if (!isTestMode) {
                await supabase.from(TABLES.CONVERSATIONS).insert([
                    {
                        assistant_id: assistant.id,
                        session_id: sessionId,
                        user_message: inputMessage,
                        assistant_response: response,
                    },
                ]);
            }

        } catch (error) {
            console.error('Error generating response:', error);
            setShowTyping(false);
            const errorMessage: ChatMessage = {
                id: (Date.now() + 1).toString(),
                role: 'assistant',
                content: 'Sorry, I encountered an error. Please try again.',
                timestamp: new Date(),
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const formatTime = (date: Date) => {
        const now = new Date();
        const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

        if (diffInHours < 24) {
            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } else {
            return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
        }
    };

    return (
        <div
            className="flex flex-col h-full bg-white"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            {/* Floating Header */}
            {showFloatingHeader && (
                <div className="top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
                    <div className="flex items-center gap-3 p-4">
                        <div
                            className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: assistant.primary_color }}
                        >
                            <Bot className="w-8 h-8 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h2 className="text-lg font-bold text-gray-900 truncate">
                                {assistant.name}'s Echoo
                            </h2>
                            <p className="text-sm text-gray-600">
                                Representing {assistant.name}, help you learn about {assistant.pronouns === 'his' ? 'him' : assistant.pronouns === 'her' ? 'her' : 'them'} on things you care.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Messages and Intro Section - Scrollable */}
            <div
                ref={messagesContainerRef}
                className={`flex-1 overflow-y-auto p-4 md:p-8 ${shouldReducePadding ? 'mt-0' : 'mt-12 md:mt-0'}`}
            >
                <div className="max-w-full mx-auto">
                    {/* Intro Section - Scrolls with messages */}
                    <div className="text-center mb-4">
                        <div
                            className={`w-32 h-32 rounded-full mx-auto flex items-center justify-center z-9
                            ${shouldReducePadding ? 'border-none relative transform-none top-0 left-0' : 'absolute transform -translate-x-1/2 left-1/2 top-48  shadow-[0_0_0_8px_white]'} 
                            md:border-none md:relative md:transform-none md:top-0 md:left-0`}
                            style={{ backgroundColor: assistant.primary_color }}
                        >
                            <Bot className="w-16 h-16 text-white" />
                        </div>
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 pt-4">
                            {assistant.name}'s Echoo
                        </h1>
                        <p className="text-md md:text-lg text-gray-600 mb-3">
                            Representing {assistant.name}, help you learn about {assistant.pronouns === 'his' ? 'him' : assistant.pronouns === 'her' ? 'her' : 'them'} on things you care.
                        </p>
                        <div className="inline-flex items-center space-x-2 text-blue-600 mb-3">
                            <Bot className="w-4 h-4" />
                            <span className="text-xs font-medium">✨</span>
                        </div>
                        <p className="text-sm text-gray-500 max-w-md mx-auto">
                            {assistant.name}'s Echoo draws from {assistant.pronouns === 'his' ? 'his' : assistant.pronouns === 'her' ? 'her' : 'their'} work, personal stories, and other's comments to provide authentic insights.
                        </p>
                        <button className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium">
                            What's Echoo?
                        </button>
                    </div>

                    {/* Messages */}
                    {messages.length > 0 && (
                        <div className="space-y-4 pt-4 border-t border-gray-100">
                            {messages.map((message) => (
                                <div
                                    key={message.id}
                                    id={`message-${message.id}`}
                                    className={`message-wrapper ${message.role === 'user' ? 'flex justify-end' : 'flex justify-start'}`}
                                >
                                    {message.role === 'assistant' ? (
                                        <div className="flex gap-3 max-w-full md:max-w-2xl">
                                            <div
                                                className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
                                                style={{ backgroundColor: assistant.primary_color }}
                                            >
                                                <Bot className="w-5 h-5 text-white" />
                                            </div>
                                            <div className="flex-1">
                                                <div className="text-gray-800">
                                                    <p className="text-base leading-relaxed break-words">
                                                        {message.content}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex justify-end max-w-full">
                                            <div className="bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 text-white rounded-3xl rounded-br-md px-4 py-3 max-w-lg ml-10">
                                                <p className="text-base leading-relaxed break-words font-semibold">
                                                    {message.content}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}

                            {showTyping && (
                                <div className="flex gap-3 max-w-full">
                                    <div
                                        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 mt-1"
                                        style={{ backgroundColor: assistant.primary_color }}
                                    >
                                        <Bot className="w-5 h-5 text-white" />
                                    </div>
                                    <div className="flex-1">
                                        <div className="text-gray-600 rounded-2xl rounded-bl-md px-4 py-3">
                                            <div className="flex items-center space-x-2">
                                                <div className="flex space-x-1">
                                                    <div className="w-2 h-2 bg-gray-400 rounded-full typing-dot"></div>
                                                    <div className="w-2 h-2 bg-gray-400 rounded-full typing-dot"></div>
                                                    <div className="w-2 h-2 bg-gray-400 rounded-full typing-dot"></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div ref={messagesEndRef} />
            </div>

            {/* Ask About Section - Same for both states */}
            <div className="border-t border-gray-100 md:px-4">
                <div className="">
                    <button
                        onClick={() => setIsAskAboutCollapsed(!isAskAboutCollapsed)}
                        className="flex items-center justify-between w-full text-left group my-4 px-4"
                    >
                        <h3 className="text-md md:text-lg font-semibold text-gray-900 group-hover:text-gray-700 transition-colors">
                            Ask about {assistant.name.split(' ')[0]}'s...
                        </h3>
                        {isAskAboutCollapsed ? (
                            <ChevronDown className="w-5 h-5 text-gray-500 group-hover:text-gray-700 transition-colors" />
                        ) : (
                            <ChevronUp className="w-5 h-5 text-gray-500 group-hover:text-gray-700 transition-colors" />
                        )}
                    </button>
                    <div className={`transition-all duration-300 ease-in-out overflow-hidden mt-4 ${isAskAboutCollapsed ? 'max-h-0 opacity-0 my-0' : 'max-h-96 opacity-100'}`}>
                        {/* Minimized version - shown when there are messages or on mobile */}
                        <div className={`px-4  block md:hidden overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100`}>
                            <div className="flex flex-col gap-2 min-w-max pb-4">
                                {/* First row */}
                                <div className="flex flex-row gap-2">
                                    {contextCards.slice(0, Math.ceil(contextCards.length / 3)).map((card) => (
                                        <ContextCardButton
                                            key={card.id}
                                            card={card}
                                            onClick={handleContextCardClick}
                                            prompt={card.prompt}
                                            isLoading={isLoading}
                                            isClicked={clickedCards.has(card.id)}
                                            variant="minimized"
                                        />
                                    ))}
                                </div>
                                {/* Second row */}
                                <div className="flex flex-row gap-2">
                                    {contextCards.slice(Math.ceil(contextCards.length / 3), Math.ceil(contextCards.length / 3) * 2).map((card) => (
                                        <ContextCardButton
                                            key={card.id}
                                            card={card}
                                            onClick={handleContextCardClick}
                                            prompt={card.prompt}
                                            isLoading={isLoading}
                                            isClicked={clickedCards.has(card.id)}
                                            variant="minimized"
                                        />
                                    ))}
                                </div>
                                {/* Third row */}
                                <div className="flex flex-row gap-2">
                                    {contextCards.slice(Math.ceil(contextCards.length / 3) * 2).map((card) => (
                                        <ContextCardButton
                                            key={card.id}
                                            card={card}
                                            onClick={handleContextCardClick}
                                            prompt={card.prompt}
                                            isLoading={isLoading}
                                            isClicked={clickedCards.has(card.id)}
                                            variant="minimized"
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Full version - shown only on tablet/desktop when no messages */}
                        <div className={`px-4 pb-6 hidden md:grid grid-cols-3 gap-3`}>
                            {contextCards.map((card) => (
                                <ContextCardButton
                                    key={card.id}
                                    card={card}
                                    onClick={handleContextCardClick}
                                    prompt={card.prompt}
                                    isLoading={isLoading}
                                    isClicked={clickedCards.has(card.id)}
                                    variant="full"
                                />
                            ))}
                        </div>
                    </div>
                </div>
                {/* Input Container - Same for both states */}
                <div className="p-4 pt-0">
                    <div className="flex items-end gap-3">
                        <div className="flex-1 relative">
                            <textarea
                                ref={textareaRef}
                                value={inputMessage}
                                onChange={(e) => setInputMessage(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder={messages.length === 0 ? `Ask anything about ${assistant.name.split(' ')[0]}` : `Message ${assistant.name}...`}
                                className="w-full resize-none rounded-2xl overflow-hidden p-4 pr-12 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 placeholder-gray-600 text-md bg-gray-100 hover:bg-gray-200"
                                rows={1}
                                disabled={isLoading}
                                style={{ minHeight: '48px', maxHeight: '120px' }}
                            />
                            {/* {inputMessage.length > 0 && (
                                <div className="absolute bottom-2 right-2 text-xs text-gray-400 bg-white px-2 py-1 rounded-full">
                                    {inputMessage.length}
                                </div>
                            )} */}
                            <button
                                onClick={handleSendMessage}
                                disabled={!inputMessage.trim() || isLoading}
                                className={`absolute bottom-4 right-2 p-2  rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${inputMessage.trim() && !isLoading
                                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white transform hover:scale-105'
                                    : 'bg-gray-200 text-gray-400 hover:bg-gray-300'
                                    }`}
                                title="Send message"
                            >
                                <Send className="w-5 h-5" />
                            </button>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChatInterface;